/**
 * environmentalApi.js — API functions for the Environmental Report page.
 *
 * fetchEnvironmentalReport       — runs the environmental report query
 * fetchEnvironmentalFilterOptions — loads filter option sets
 */

import { apiFetch } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => ({
    'Content-Type': 'application/json',
    'x-amz-security-token': import.meta.env.VITE_AUTH_TOKEN,
});

/** Device category filter auto-set by environment type */
function getDeviceCategoryFilter(environTypeId) {
    if (environTypeId === 1) return '1,17';   // Temperature
    if (environTypeId === 2) return '17';      // Humidity
    return '1';                                // Battery (3 & 4)
}

// ---------------------------------------------------------------------------
// Fetch Environmental Report
// ---------------------------------------------------------------------------

/**
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — YYYY-MM-DD
 * @param {string} opts.endDate     — YYYY-MM-DD
 * @param {number} opts.environTypeId — 1=Temp, 2=Humidity, 3=CustomBattery, 4=FullBattery
 * @param {object} opts.filters
 * @returns {Promise<object>} — { records, summary, overlimit, url, ErrorCode }
 */
export async function fetchEnvironmentalReport({ campusId, username, startDate, endDate, environTypeId, filters = {} }) {
    const body = {
        rep_type: 'environment_report',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        device_category_filter: getDeviceCategoryFilter(environTypeId),
        area_type_filter: filters.areaTypes || '',
        reason_filter: filters.reasons || '',
        user_filter: filters.users || '',
        event_type_filter: filters.eventTypes || '',
        resident_filter: filters.residents || '',
        resident_group_filter: '',
        area_filter: filters.areas || '',
        area_group_filter: '',
        user_group_filter: '',
        environment_type: environTypeId,
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        if (resp.status === 503) return { ErrorCode: 11 };
        if (resp.status === 500) return { ErrorCode: 10 };
        if (resp.status === 403) return { ErrorCode: 12 };

        const raw = await resp.json();
        if (raw?.ErrorCode) return { ErrorCode: raw.ErrorCode };

        const data = raw?.Res || raw;
        if (data?.ErrorCode) return { ErrorCode: data.ErrorCode };

        return {
            records: data?.records || [],
            summary: data?.summary || [],
            overlimit: Boolean(data?.overlimit),
            url: data?.url || '',
        };
    } catch (err) {
        console.error('[environmentalApi] fetchEnvironmentalReport error:', err);
        return { ErrorCode: 10 };
    }
}

// ---------------------------------------------------------------------------
// Fetch Filter Options
// ---------------------------------------------------------------------------

/**
 * @param {number} campusId
 * @returns {Promise<object>}
 */
export async function fetchEnvironmentalFilterOptions(campusId) {
    try {
        const [portalFilters, eventTypes, reasonCodes] = await Promise.all([
            fetchPortalFilters(campusId),
            apiFetch({
                table: 'att_event_types',
                procedure: 'fetchall',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
            apiFetch({
                table: 'core_reason_codes',
                procedure: 'fetchbyparameter',
                reqType: 'direct',
                pKey: 'campus_id',
                pVal: campusId,
            }),
        ]);

        const residentsWithName = (portalFilters.residents || []).map((r) => ({
            ...r,
            resident_name: `${r.resident_first_name || ''} ${r.resident_last_name || ''}`.trim() || '(unknown)',
        }));

        return {
            areas: portalFilters.areas || [],
            areaTypes: portalFilters.areaTypes || [],
            residents: residentsWithName,
            users: portalFilters.users || [],
            eventTypes: eventTypes || [],
            reasonCodes: reasonCodes || [],
        };
    } catch (err) {
        console.error('[environmentalApi] fetchEnvironmentalFilterOptions error:', err);
        return {};
    }
}

async function fetchPortalFilters(campusId) {
    const body = {
        rep_type: 'getportalfilters',
        campus_id: campusId,
        device_category_filter: '1',
        area_type_filter: '1',
        resident_filter: '1',
        area_filter: '1',
        area_group_filter: '',
        resident_group_filter: '',
        user_filter: '1',
        user_group_filter: '',
    };

    try {
        const resp = await fetch(getBaseUrl() + import.meta.env.VITE_MY_REPORTS_ENDPOINT, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body),
        });

        const raw = await resp.json();
        const data = raw?.Res || raw;

        return {
            areas: data?.areas || [],
            areaTypes: data?.area_types || [],
            residents: data?.residents || [],
            users: data?.users || [],
        };
    } catch (err) {
        console.error('[environmentalApi] fetchPortalFilters error:', err);
        return {};
    }
}
